import { ModalButton } from '@/components/common/modal-button';
import { NavigationDirection } from '@/components/feature/floor-guide';
import { PauseIcon, PlayIcon, StopIcon } from '@/components/icons/floor-guide';
import { useAccessibilityTTS, useKeyboardNavigation, useScreenSize, useTickSoundInteraction } from '@/hooks';
import { useConfigStore } from '@/stores/configStore';
import type { Direction, NavigationGuideEndPoint } from '@/types/floor-guide';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useFocusTrap } from '@/hooks/modal';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import styles from './styles.module.scss';
import { AppUtils } from '@/utils/app';
import clsx from 'clsx';
import { MODAL_IDS } from '@/constants/modal-stack';
import { useModalStackStore } from '@/stores/modalStackStore';

type Props = {
    qrHereUrl: string;
    directions: Direction[];
    buildingName: string;
    startFloorName: string;
    endPoint: NavigationGuideEndPoint;

    isShowPauseBtn?: boolean;
    isShowWatchAgainBtn?: boolean;
    isShowResumeBtn?: boolean;
    isActive?: boolean;
    directionMaxVisibleRows?: number;
    onPause?: () => void;
    onResume?: () => void;
    onWatchAgain?: () => void;
    onStop: () => void;
};

export const NavigationGuide = ({
    qrHereUrl,
    directions,
    buildingName,
    startFloorName,
    endPoint,
    onPause,
    onResume,
    onWatchAgain,
    onStop,
    isShowPauseBtn,
    isShowWatchAgainBtn,
    isShowResumeBtn,
    isActive = true,
    directionMaxVisibleRows,
}: Props) => {
    const idModal = MODAL_IDS.NAVIGATION_GUIDE;
    const { t } = useTranslation();
    const { scaleHeightPixel } = useScreenSize();
    const navigationGuideRef = useRef<HTMLDivElement>(null);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const { speak, speakSequentially } = useAccessibilityTTS();
    const shouldRefocusRef = useRef<boolean>(false);
    const qrCodeRef = useRef<HTMLDivElement>(null);
    const isQrhere = useConfigStore((state) => !!state.config.server?.store?.qrHereId || false);
    const isKeyboardNavigation = useKeyboardNavigation();
    const { playTick } = useTickSoundInteraction();
    const isFirstOpenRef = useRef(true);
    const [, setAriaText] = useState<string>('');
    const { pushModal, popModal, isModalInStack } = useModalStackStore();

    const mergedArialLabel = `${t('floor_guide.text_to_speech.starting_route_guidance')}, ${t('floor_guide.button.direction_guide_tts')}`;

    const arialLabel = `${t('floor_guide.button.direction_guide_tts')}`;

    useEffect(() => {
        if (!isActive) {
            isFirstOpenRef.current = true;
            setAriaText(arialLabel);
            return;
        }

        if (isFirstOpenRef.current) {
            setAriaText(mergedArialLabel);
            isFirstOpenRef.current = false;

            const timeoutId = setTimeout(() => {
                setAriaText(arialLabel);
            }, 500);

            return () => clearTimeout(timeoutId);
        } else {
            setAriaText(arialLabel);
        }
    }, [isActive, mergedArialLabel, arialLabel]);

    useEffect(() => {
        if (isActive) {
            setTimeout(() => {
                if (!isModalInStack(idModal)) {
                    pushModal(idModal);
                }
            }, 50);
        } else {
            if (isModalInStack(idModal)) {
                popModal(idModal);
            }
        }

        return () => {
            if (isModalInStack(idModal)) {
                popModal(idModal);
            }
        };
    }, [isActive, idModal, pushModal, popModal, isModalInStack]);

    useFocusTrap(navigationGuideRef, isActive, onStop, false, idModal, true, false);

    // Refocus vào container sau khi các button thay đổi
    useEffect(() => {
        if (shouldRefocusRef.current && navigationGuideRef.current) {
            // Tìm button đầu tiên trong actions section để focus
            const actionsSection = navigationGuideRef.current.querySelector(`.${styles['navigation-guide__actions']}`);
            if (actionsSection) {
                const firstButton = actionsSection.querySelector('button') as HTMLElement;
                if (firstButton) {
                    setTimeout(() => {
                        firstButton.focus();
                        shouldRefocusRef.current = false;
                    }, 50);
                }
            }
        }
        // Remove else clause - useFocusTrap handles initial focus now
    }, [isShowPauseBtn, isShowWatchAgainBtn, isShowResumeBtn]);

    // Helper function để handle click và refocus
    const handleActionClick = (callback?: () => void) => {
        shouldRefocusRef.current = true;
        callback?.();
    };

    const departureArialLabel = useMemo(() => {
        return `${t('floor_guide.label.depart')} ${t('floor_guide.label.current_location')} ${Array.isArray(buildings) && buildings.length > 1 ? buildingName : ''} ${t('floor_guide.text_to_speech.floor', { floorName: AppUtils.getFloorTTSText(startFloorName) })}`;
    }, [buildingName, startFloorName, t, buildings]);

    const destinationArialLabel = useMemo(() => {
        return `${t('floor_guide.label.arrive')} ${endPoint.poiName} ${Array.isArray(buildings) && buildings.length > 1 ? buildingName : ''} ${t('floor_guide.text_to_speech.floor', { floorName: AppUtils.getFloorTTSText(endPoint.floorName) })}`;
    }, [endPoint.poiName, endPoint.floorName, buildingName, t, buildings]);

    return (
        <div ref={navigationGuideRef} role="dialog" aria-modal="true">
            <div className={styles['navigation-guide']} aria-label={t('floor_guide.label.navigation_guide')}>
                {isQrhere && (
                    <div
                        ref={qrCodeRef}
                        data-modal-region
                        className={styles['navigation-guide__qr-code'] + ' focus-border'}
                        tabIndex={0}
                        data-interactive
                        aria-label={t('floor_guide.text_to_speech.view_route_on_mobile_qr')}
                    >
                        <QRCode value={qrHereUrl} size={scaleHeightPixel(60)} />
                        <p>{t('floor_guide.title.view_route_on_mobile')}</p>
                    </div>)}

                {/* Departure Point */}
                <div
                    className={styles['navigation-guide__departure-point'] + ' focus-border'}
                    data-modal-region
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    data-interactive
                    aria-label={departureArialLabel}
                >
                    <div className={styles['navigation-guide__departure-point__label']}>{t('floor_guide.label.depart')}</div>
                    <div className={styles['navigation-guide__departure-point__content']}>
                        <div className={styles['location-name']}>{t('floor_guide.label.current_location')}</div>
                        <div className={styles['location-info']}>
                            {Array.isArray(buildings) && buildings.length > 1 && <span className={styles['location-info__building-name']}>{buildingName}</span>}
                            <span className={clsx(styles['location-info__floor-name'], 'number')}>{startFloorName}</span>
                        </div>
                    </div>
                </div>

                {/* Directions */}
                <div className={styles['navigation-guide__directions']}>
                    <NavigationDirection directions={directions} maxVisibleRows={directionMaxVisibleRows} />
                </div>
                {/* Destination Point */}
                <div
                    className={styles['navigation-guide__destination-point'] + ' focus-border'}
                    data-modal-region
                    tabIndex={0}
                    data-interactive
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    aria-label={destinationArialLabel}
                >
                    <div className={styles['navigation-guide__destination-point__label']}>{t('floor_guide.label.arrive')}</div>
                    <div className={styles['navigation-guide__destination-point__content']}>
                        <div className={clsx(styles['location-name'], 'line-clamp-1 text-ellipsis')}>{endPoint.poiName}</div>
                        <div className={styles['location-info']}>
                            {Array.isArray(buildings) && buildings.length > 1 && (
                                <span className={styles['location-info__building-name']}>{endPoint.buildingName}</span>
                            )}
                            <span className={clsx('number', styles['location-info__floor-name'])}>{endPoint.floorName}</span>
                        </div>
                    </div>
                </div>
                {/* Actions */}
                <div className={styles['navigation-guide__actions']}>
                    {isShowPauseBtn && (
                        <ModalButton
                            data-modal-region
                            size="sm"
                            icon={<PauseIcon />}
                            tabIndex={0}
                            role="button"
                            data-tts-scope="navigation-guide-button"
                            onFocus={() => {
                                if (isKeyboardNavigation) {
                                    speakSequentially(t('floor_guide.text_to_speech.focus.pause_btn'));
                                }
                            }}
                            onClick={() => {
                                speakSequentially(t('floor_guide.text_to_speech.click.pause_btn'));
                                handleActionClick(onPause);
                            }}
                        >
                            {t('floor_guide.button.pause')}
                        </ModalButton>
                    )}
                    {isShowWatchAgainBtn && (
                        <ModalButton
                            data-modal-region
                            size="sm"
                            icon={<PlayIcon />}
                            tabIndex={0}
                            role="button"
                            data-tts-scope="navigation-guide-button"
                            onFocus={() => {
                                if (isKeyboardNavigation) {
                                    speakSequentially(t('floor_guide.text_to_speech.focus.watch_again_btn'));
                                }
                            }}
                            onClick={() => {
                                speakSequentially(t('floor_guide.text_to_speech.click.watch_again_btn'));
                                handleActionClick(onWatchAgain);
                            }}
                        >
                            {t('floor_guide.button.watch_again')}
                        </ModalButton>
                    )}
                    {isShowResumeBtn && (
                        <ModalButton
                            data-modal-region
                            size="sm"
                            icon={<PlayIcon />}
                            tabIndex={0}
                            role="button"
                            data-tts-scope="navigation-guide-button"
                            onFocus={() => {
                                if (isKeyboardNavigation) {
                                    speakSequentially(t('floor_guide.text_to_speech.focus.resume_btn'));
                                }
                            }}
                            onClick={() => {
                                speakSequentially(t('floor_guide.text_to_speech.click.resume_btn'));
                                handleActionClick(onResume);
                            }}
                        >
                            {t('floor_guide.button.resume')}
                        </ModalButton>
                    )}
                    <ModalButton
                        data-modal-region
                        size="sm"
                        icon={<StopIcon />}
                        variant="secondary"
                        tabIndex={0}
                        role="button"
                        data-interactive
                        aria-label={t('floor_guide.text_to_speech.stop')}
                        data-role={t('common.data_role.button')}
                        data-tts-scope="navigation-guide-button"
                        onFocus={() => {
                            if (isKeyboardNavigation) {
                                speak(t('floor_guide.text_to_speech.focus.stop_btn'));
                            }
                        }}
                        onClick={() => {
                            onStop?.();
                        }}
                    >
                        {t('floor_guide.button.stop')}
                    </ModalButton>
                </div>
            </div>
        </div>
    );
};
